/**
 * Copyright (c) 2021, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
 * You may not alter or remove any copyright or other notice from copies of this content.
 */

import { AuthProvider } from "@asgardeo/auth-react";
import React, { FunctionComponent, ReactElement } from "react";
import { Route, BrowserRouter as Router } from "react-router-dom";
import { SecureRouteWithRedirect } from "./secure-route-with-redirect";
import { useConfig } from "../hooks";
import { HomePage } from "../pages";
import { ErrorPage } from "../pages/error";
import { LoginPage } from "../pages/log-in-page";

/**
 * app with client identifier props interface 
 */
export interface AppWithClientIdentifierPropsInterface {
    /**
     * Client Identifier of the application.
     */
    clientId: string;
    /**
     * Tenant Identifier of the application.
     */
    tenant: string;
    /**
     * Base URL of the application.
     */
    baseUrl: string;
}

/**
 * Main App component.
 *
 * @returns ReactElement
 */
export const AppWithClientIdentifier: FunctionComponent<AppWithClientIdentifierPropsInterface> = (
    props: AppWithClientIdentifierPropsInterface
): ReactElement => {

    const  {
        clientId,
        baseUrl
    } = props;

    const { deployment } = useConfig();

    if (!clientId) {
        return <div>No Client ID Defined</div>;
    }

    return (
        <AuthProvider
            fallback={ <LoginPage data-componentid="login-page" clientID= { clientId } /> }
            config={ {
                baseUrl: baseUrl,
                clientID: clientId,
                scope: [ "openid", "profile","email" ],
                signInRedirectURL: window.location.origin,
                signOutRedirectURL: window.location.origin,
                ...deployment?.idpConfigs
            } }
        >
            <Router>
                <Route path="/" exact>
                    <LoginPage data-componentid="login-page" clientID={ clientId } />
                </Route>
                <SecureRouteWithRedirect exact path="/home" component={ HomePage } />
                <Route exact path="/error">
                    <ErrorPage data-componentid="error-page" />
                </Route>
            </Router>
        </AuthProvider>
    );
};
